import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Progress } from 'components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from 'components/ui/dialog';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { Checkbox } from 'components/ui/checkbox';
import { 
  ArrowLeft, Shield, CheckCircle, AlertTriangle, Clock, MapPin, 
  FileText, Download, Filter, TrendingUp, AlertCircle, Loader2,
  ClipboardCheck, User, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const SERVICE_TYPES = {
  personal_care: "Personal Care",
  homemaker: "Homemaker",
  companion: "Companion",
  respite: "Respite Care",
  skilled_nursing: "Skilled Nursing",
  therapy: "Therapy",
  transportation: "Transportation",
  meal_prep: "Meal Prep",
  medication_assist: "Medication Assist",
  other: "Other"
};

const TASKS = [
  "Bathing/Grooming",
  "Dressing",
  "Meal Preparation",
  "Medication Reminders",
  "Light Housekeeping",
  "Laundry",
  "Transportation",
  "Companionship",
  "Mobility Assistance",
  "Exercise/PT Support"
];

export default function EVVCompliance() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [complianceReport, setComplianceReport] = useState(null);
  const [visits, setVisits] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [realtimeAlerts, setRealtimeAlerts] = useState(null);
  const [anomalies, setAnomalies] = useState(null);
  const [period, setPeriod] = useState('month');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');
  
  // Verification modal
  const [verifyModal, setVerifyModal] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [serviceType, setServiceType] = useState('personal_care');
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  useEffect(() => {
    fetchData();
  }, [period, statusFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reportRes, visitsRes, alertsRes, realtimeRes, anomaliesRes] = await Promise.all([
        api.get(`/evv/compliance-report?period=${period}`),
        api.get(`/evv/visits?status=${statusFilter}`),
        api.get(`/evv/alerts`),
        api.get(`/evv/realtime-alerts`),
        api.get(`/evv/anomalies?period=${period}`)
      ]);
      
      setComplianceReport(reportRes.data);
      setVisits(visitsRes.data.visits || []);
      setAlerts(alertsRes.data.alerts || []);
      setRealtimeAlerts(realtimeRes.data);
      setAnomalies(anomaliesRes.data);
    } catch (error) {
      console.error('Failed to fetch EVV data:', error);
      toast.error('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!selectedVisit) return;
    
    setSubmitting(true);
    try {
      await api.post(`/evv/visits/${selectedVisit.shift_id}/verify`, {
        service_type: serviceType,
        tasks_completed: selectedTasks,
        notes
      });
      
      toast.success('Visit verified successfully');
      setVerifyModal(false);
      setSelectedVisit(null);
      setSelectedTasks([]);
      setNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify visit');
    } finally {
      setSubmitting(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get(`/evv/export?format=csv`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `evv_report_${format(new Date(), 'yyyy-MM-dd')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Report exported successfully');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'MMM d, yyyy h:mm a');
    } catch {
      return dateStr;
    }
  };

  const metrics = complianceReport?.metrics || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <Button variant="ghost" size="icon" className="shrink-0" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 font-['Manrope']">EVV Compliance</h1>
              <p className="text-xs sm:text-sm text-slate-500 hidden sm:block">Electronic Visit Verification</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-28 sm:w-32 h-8 sm:h-9 text-xs sm:text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
                <SelectItem value="quarter">Quarter</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" className="hidden sm:flex" onClick={handleExport} data-testid="export-btn">
              <Download className="w-4 h-4" />
              Export
            </Button>
            <Button variant="outline" size="icon" className="sm:hidden h-8 w-8" onClick={handleExport} data-testid="export-btn-mobile">
              <Download className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-4 py-4 sm:py-8 space-y-4 sm:space-y-6">
          {/* Real-time Alerts Banner */}
          {realtimeAlerts?.summary?.high > 0 && (
            <div className="p-4 rounded-lg border bg-red-50 border-red-200 flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertCircle className="w-5 h-5 text-red-500" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-red-800">Active Alerts Require Attention</p>
                <p className="text-sm text-red-700">
                  {realtimeAlerts.summary.high} high priority, {realtimeAlerts.summary.warning} warnings
                </p>
              </div>
              <Button 
                variant="outline" 
                className="border-red-300 text-red-700 hover:bg-red-100"
                onClick={() => setActiveTab('realtime')}
              >
                View Alerts
              </Button>
            </div>
          )}

          {/* Tab Navigation */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full">
              <TabsTrigger value="overview" data-testid="tab-overview" className="text-xs sm:text-sm flex-1">Overview</TabsTrigger>
              <TabsTrigger value="realtime" data-testid="tab-realtime" className="text-xs sm:text-sm flex-1">
                Alerts
                {realtimeAlerts?.summary?.total > 0 && (
                  <Badge className="ml-1 bg-red-500 text-[10px] px-1.5">{realtimeAlerts.summary.total}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="visits" data-testid="tab-visits" className="text-xs sm:text-sm flex-1">Visits</TabsTrigger>
              <TabsTrigger value="anomalies" data-testid="tab-anomalies" className="text-xs sm:text-sm flex-1">
                Anomalies
                {anomalies?.anomalies?.length > 0 && (
                  <Badge variant="secondary" className="ml-1 text-[10px] px-1.5">{anomalies.anomalies.length}</Badge>
                )}
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              {/* Alerts */}
              {alerts.length > 0 && (
                <div className="space-y-3">
                  {alerts.map((alert, idx) => (
                    <div 
                      key={idx}
                      className={`p-4 rounded-lg border flex items-start gap-3 ${
                        alert.severity === 'high' 
                          ? 'bg-red-50 border-red-200' 
                          : 'bg-amber-50 border-amber-200'
                      }`}
                    >
                      <AlertTriangle className={`w-5 h-5 mt-0.5 ${
                        alert.severity === 'high' ? 'text-red-500' : 'text-amber-500'
                      }`} />
                      <div className="flex-1">
                        <p className={`font-medium ${
                          alert.severity === 'high' ? 'text-red-800' : 'text-amber-800'
                        }`}>{alert.title}</p>
                        <p className={`text-sm ${
                          alert.severity === 'high' ? 'text-red-700' : 'text-amber-700'
                        }`}>{alert.message}</p>
                      </div>
                      <Badge variant="outline" className={
                        alert.severity === 'high' ? 'border-red-300 text-red-700' : 'border-amber-300 text-amber-700'
                      }>
                        {alert.count}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}

              {/* Compliance Score Card */}
              <Card>
                <CardContent className="p-4 sm:p-6">
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4 sm:gap-6">
                    {/* Main Score */}
                    <div className="md:col-span-2 flex flex-col items-center justify-center p-4 sm:p-6 bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl">
                      <div className="text-3xl sm:text-5xl font-bold text-teal-600">{metrics.compliance_score || 0}%</div>
                      <p className="text-teal-700 font-medium mt-2 text-sm sm:text-base">Compliance Score</p>
                      <Progress value={metrics.compliance_score || 0} className="w-full mt-4 h-2" />
                    </div>

                    {/* Metrics Grid */}
                    <div className="md:col-span-3 grid grid-cols-3 gap-2 sm:gap-4">
                      <div className="p-2.5 sm:p-4 bg-slate-50 rounded-lg text-center">
                        <div className="text-lg sm:text-2xl font-bold text-slate-900">{metrics.total_visits || 0}</div>
                        <p className="text-xs sm:text-sm text-slate-500">Visits</p>
                      </div>
                      <div className="p-2.5 sm:p-4 bg-green-50 rounded-lg text-center">
                        <div className="text-lg sm:text-2xl font-bold text-green-600">{metrics.verified_visits || 0}</div>
                        <p className="text-xs sm:text-sm text-green-700">Verified</p>
                      </div>
                      <div className="p-2.5 sm:p-4 bg-blue-50 rounded-lg text-center">
                        <div className="text-lg sm:text-2xl font-bold text-blue-600">{metrics.location_verified || 0}</div>
                        <p className="text-xs sm:text-sm text-blue-700">GPS</p>
                      </div>
                      <div className="p-2.5 sm:p-4 bg-purple-50 rounded-lg text-center">
                        <div className="text-lg sm:text-2xl font-bold text-purple-600">{metrics.total_hours || 0}h</div>
                        <p className="text-xs sm:text-sm text-purple-700">Hours</p>
                      </div>
                      <div className="p-2.5 sm:p-4 bg-teal-50 rounded-lg text-center">
                        <div className="text-lg sm:text-2xl font-bold text-teal-600">{complianceReport?.verification_rate || 0}%</div>
                        <p className="text-xs sm:text-sm text-teal-700">Verified</p>
                      </div>
                      <div className="p-2.5 sm:p-4 bg-orange-50 rounded-lg text-center">
                        <div className="text-lg sm:text-2xl font-bold text-orange-600">{complianceReport?.location_rate || 0}%</div>
                        <p className="text-xs sm:text-sm text-orange-700">Location</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Real-time Alerts Tab */}
            <TabsContent value="realtime" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                    Real-time Alerts
                  </CardTitle>
                  <CardDescription>Active issues that require immediate attention</CardDescription>
                </CardHeader>
                <CardContent>
                  {realtimeAlerts?.alerts?.length === 0 ? (
                    <div className="text-center py-12">
                      <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-4" />
                      <p className="text-green-600 font-medium">All Clear!</p>
                      <p className="text-slate-500">No active alerts at this time</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {realtimeAlerts?.alerts?.map((alert) => (
                        <div 
                          key={alert.alert_id}
                          className={`p-4 rounded-lg border ${
                            alert.severity === 'high' 
                              ? 'bg-red-50 border-red-200' 
                              : alert.severity === 'warning'
                              ? 'bg-amber-50 border-amber-200'
                              : 'bg-blue-50 border-blue-200'
                          }`}
                        >
                          <div className="flex items-start gap-2 sm:gap-3">
                            {alert.severity === 'high' ? (
                              <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-500 mt-0.5 shrink-0" />
                            ) : alert.severity === 'warning' ? (
                              <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500 mt-0.5 shrink-0" />
                            ) : (
                              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500 mt-0.5 shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <p className={`font-medium text-sm sm:text-base ${
                                  alert.severity === 'high' ? 'text-red-800'
                                  : alert.severity === 'warning' ? 'text-amber-800'
                                  : 'text-blue-800'
                                }`}>{alert.title}</p>
                                <Badge className={`shrink-0 text-[10px] sm:text-xs ${
                                  alert.severity === 'high' ? 'bg-red-500'
                                  : alert.severity === 'warning' ? 'bg-amber-500'
                                  : 'bg-blue-500'
                                }`}>
                                  {alert.severity}
                                </Badge>
                              </div>
                              <p className={`text-xs sm:text-sm ${
                                alert.severity === 'high' ? 'text-red-700'
                                : alert.severity === 'warning' ? 'text-amber-700'
                                : 'text-blue-700'
                              }`}>{alert.message}</p>
                              {alert.action && (
                                <p className="text-xs text-slate-500 mt-1">
                                  <span className="font-medium">Action:</span> {alert.action}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Visits Tab */}
            <TabsContent value="visits">
              <Card>
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <CardTitle className="text-base sm:text-lg">Visit Records</CardTitle>
                      <CardDescription className="text-xs sm:text-sm">EVV-compliant visit documentation</CardDescription>
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-32 sm:w-40 shrink-0 h-8 text-xs sm:text-sm">
                        <Filter className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" />
                        <SelectValue placeholder="Filter" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Visits</SelectItem>
                        <SelectItem value="verified">Verified</SelectItem>
                        <SelectItem value="unverified">Unverified</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardHeader>
                <CardContent>
                  {visits.length === 0 ? (
                    <div className="text-center py-12">
                      <ClipboardCheck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">No visits found for the selected period</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {visits.slice(0, 20).map((visit) => (
                        <div
                          key={visit.shift_id}
                          className="p-3 sm:p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shrink-0 ${
                              visit.evv_verified ? 'bg-green-100' : 'bg-amber-100'
                            }`}>
                              {visit.evv_verified ? (
                                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                              ) : (
                                <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-slate-900 text-sm sm:text-base">{visit.recipient_name}</span>
                                <Badge variant="outline" className="text-[10px] sm:text-xs">
                                  {SERVICE_TYPES[visit.evv_data?.service_type] || 'Personal Care'}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-slate-500 mt-0.5 flex-wrap">
                                <span className="flex items-center gap-1">
                                  <User className="w-3 h-3 shrink-0" />
                                  {visit.caregiver_name}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3 shrink-0" />
                                  {formatDate(visit.clock_in)}
                                </span>
                                <span>{visit.total_hours?.toFixed(1) || 0}h</span>
                              </div>
                              <div className="flex items-center gap-2 mt-2">
                                {visit.location_verified ? (
                                  <Badge className="bg-blue-100 text-blue-700 text-[10px] sm:text-xs">
                                    <MapPin className="w-3 h-3" />
                                    GPS ✓
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="text-slate-500 text-[10px] sm:text-xs">
                                    <MapPin className="w-3 h-3" />
                                    No GPS
                                  </Badge>
                                )}
                                {visit.evv_verified ? (
                                  <Badge className="bg-green-100 text-green-700 text-[10px] sm:text-xs">EVV ✓</Badge>
                                ) : (
                                  <Button
                                    size="sm"
                                    onClick={() => {
                                      setSelectedVisit(visit);
                                      setVerifyModal(true);
                                    }}
                                    className="bg-teal-500 hover:bg-teal-600 h-7 text-xs"
                                    data-testid={`verify-${visit.shift_id}`}
                                  >
                                    Verify
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Anomalies Tab */}
            <TabsContent value="anomalies">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-purple-500" />
                    Anomaly Detection
                  </CardTitle>
                  <CardDescription>
                    Patterns and anomalies detected from {anomalies?.total_shifts_analyzed || 0} shifts analyzed
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {anomalies?.anomalies?.length === 0 ? (
                    <div className="text-center py-12">
                      <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-4" />
                      <p className="text-green-600 font-medium">No Anomalies Detected</p>
                      <p className="text-slate-500">All patterns appear normal</p>
                    </div>
                  ) : (
                    <div className="space-y-3 sm:space-y-4">
                      {anomalies?.anomalies?.map((anomaly, idx) => (
                        <div
                          key={idx}
                          className={`p-3 sm:p-4 rounded-lg border ${
                            anomaly.severity === 'high'
                              ? 'bg-red-50 border-red-200'
                              : anomaly.severity === 'warning'
                              ? 'bg-amber-50 border-amber-200'
                              : 'bg-blue-50 border-blue-200'
                          }`}
                        >
                          <div className="flex items-start gap-2 sm:gap-3">
                            <div className={`p-1.5 sm:p-2 rounded-full shrink-0 ${
                              anomaly.severity === 'high' ? 'bg-red-100'
                              : anomaly.severity === 'warning' ? 'bg-amber-100'
                              : 'bg-blue-100'
                            }`}>
                              {anomaly.type === 'excessive_hours' && <Clock className="w-4 h-4 text-amber-600" />}
                              {anomaly.type === 'frequent_long_shifts' && <TrendingUp className="w-4 h-4 text-blue-600" />}
                              {anomaly.type === 'location_issues' && <MapPin className="w-4 h-4 text-red-600" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <p className="font-medium text-slate-900 text-sm sm:text-base">{anomaly.title}</p>
                                <Badge className={`shrink-0 text-[10px] sm:text-xs ${
                                  anomaly.severity === 'high' ? 'bg-red-500'
                                  : anomaly.severity === 'warning' ? 'bg-amber-500'
                                  : 'bg-blue-500'
                                }`}>
                                  {anomaly.severity}
                                </Badge>
                              </div>
                              <p className="text-xs sm:text-sm text-slate-600 mt-1">{anomaly.description}</p>
                              <div className="mt-2 p-2 bg-white rounded border">
                                <p className="text-xs text-slate-500">
                                  <span className="font-medium">Recommendation:</span> {anomaly.recommendation}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      )}

      {/* Verify Modal */}
      <Dialog open={verifyModal} onOpenChange={setVerifyModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-teal-500" />
              Verify Visit for EVV
            </DialogTitle>
          </DialogHeader>
          
          {selectedVisit && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Visit Details</p>
                <p className="font-medium">{selectedVisit.recipient_name}</p>
                <p className="text-sm text-slate-600">{formatDate(selectedVisit.clock_in)}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Service Type</Label>
                <Select value={serviceType} onValueChange={setServiceType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(SERVICE_TYPES).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Tasks Completed</Label>
                <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 border rounded-lg">
                  {TASKS.map((task) => (
                    <div key={task} className="flex items-center gap-2">
                      <Checkbox
                        id={task}
                        checked={selectedTasks.includes(task)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedTasks([...selectedTasks, task]);
                          } else {
                            setSelectedTasks(selectedTasks.filter(t => t !== task));
                          }
                        }}
                      />
                      <label htmlFor={task} className="text-sm cursor-pointer">{task}</label>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any additional notes about the visit..."
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setVerifyModal(false)}>Cancel</Button>
            <Button 
              onClick={handleVerify}
              disabled={submitting}
              className="bg-teal-500 hover:bg-teal-600"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              Verify Visit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
